/* eslint-disable react/no-unescaped-entities */
import LegalLayout, { Section, LEGAL } from "./LegalLayout";

export default function PrivacyPolicy() {
  return (
    <LegalLayout title="Privacy Policy" testid="page-privacy">
      <p>
        {LEGAL.brand} ("we", "us", "our") respects your privacy. This Privacy Policy explains what information we collect when you visit {LEGAL.site}, how we use it, and the choices available to you.
      </p>

      <Section id="data" title="1. Information We Collect">
        <p>We collect only the minimum information required to operate the Service:</p>
        <ul className="list-disc pr-5 space-y-1.5">
          <li><span className="text-white font-semibold">Order data</span> — email address (when you provide it at checkout), purchased product, amount, currency, country, and an anonymous order token.</li>
          <li><span className="text-white font-semibold">Payment data</span> — handled exclusively by our payment provider (e.g. Stripe). We never receive or store full card numbers, CVV, or expiry dates.</li>
          <li><span className="text-white font-semibold">Usage analytics</span> — anonymised visit, click, FAQ-open and time-on-page events, plus an opaque browser identifier (no name or email is attached).</li>
          <li><span className="text-white font-semibold">Marketing pixels</span> — TikTok Pixel and Google Ads conversion tracking, with hashed identifiers (email, external_id) used only for conversion attribution.</li>
        </ul>
      </Section>

      <Section id="use" title="2. How We Use Your Information">
        <ul className="list-disc pr-5 space-y-1.5">
          <li>To deliver the products you purchase and provide customer support.</li>
          <li>To detect, prevent, and respond to fraud, abuse, or security incidents.</li>
          <li>To measure marketing performance and improve our funnels.</li>
          <li>To comply with applicable laws, tax requirements, and lawful requests.</li>
        </ul>
        <p>We do not sell your personal information.</p>
      </Section>

      <Section id="share" title="3. Who We Share Data With">
        <p>We share the strictly necessary data with a small set of trusted processors:</p>
        <ul className="list-disc pr-5 space-y-1.5">
          <li><span className="text-white font-semibold">Payment processors</span> (Stripe and any future Mada/Tap/Moyasar processor) — to process your payment.</li>
          <li><span className="text-white font-semibold">Analytics / advertising</span> (TikTok, Google Ads) — for hashed conversion measurement.</li>
          <li><span className="text-white font-semibold">Hosting and infrastructure</span> — to run the website and store order records.</li>
        </ul>
        <p>Each processor is bound by its own data-protection terms and is permitted to process your data only on our documented instructions.</p>
      </Section>

      <Section id="retention" title="4. How Long We Keep Data">
        <p>Order and payment records are retained for the period required by applicable tax and accounting laws (typically 5–7 years). Anonymised analytics events are retained for up to 24 months. You may request earlier deletion where the law permits.</p>
      </Section>

      <Section id="cookies" title="5. Cookies and Local Storage">
        <p>We use a small number of first-party cookies and localStorage entries to remember your session, your selected bundle, and an anonymous visitor id used for analytics. We also load TikTok and Google Ads pixel scripts that may set their own cookies; refer to those providers' policies for details.</p>
      </Section>

      <Section id="rights" title="6. Your Rights">
        <p>Depending on your jurisdiction (including GDPR / UK GDPR / Saudi PDPL), you have the right to:</p>
        <ul className="list-disc pr-5 space-y-1.5">
          <li>Access the personal data we hold about you.</li>
          <li>Request correction or deletion.</li>
          <li>Object to or restrict certain processing.</li>
          <li>Receive a portable copy of your data.</li>
          <li>Withdraw consent at any time (without affecting prior lawful processing).</li>
        </ul>
        <p>To exercise any of these rights, email <a href={`mailto:${LEGAL.contactEmail}`} className="text-[#C9A66A]">{LEGAL.contactEmail}</a> from the address associated with your purchase.</p>
      </Section>

      <Section id="children" title="7. Children">
        <p>The Service is not directed to anyone under 18. We do not knowingly collect data from children. If you believe a minor has provided us data, contact us and we will delete it.</p>
      </Section>

      <Section id="security" title="8. Security">
        <p>We use TLS encryption in transit, restricted access controls, and reputable hosting and payment providers. No system is perfectly secure; we encourage you to keep your account and device hygiene strong.</p>
      </Section>

      <Section id="changes" title="9. Changes to This Policy">
        <p>We may update this Policy from time to time. Changes are reflected by updating the "Last updated" date above. Material changes will be highlighted on the site.</p>
      </Section>

      <Section id="contact" title="10. Contact">
        <p>Questions or requests? Email <a href={`mailto:${LEGAL.contactEmail}`} className="text-[#C9A66A]">{LEGAL.contactEmail}</a>.</p>
      </Section>
    </LegalLayout>
  );
}
